// PATH: src/components/ui/feedback.tsx
/**
 * Retours utilisateur communs : un toast (`useToast`) et une confirmation
 * (`useConfirm`) à la place de `window.confirm`. Un seul de chaque dans l'app.
 */
import * as React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

type Severity = "success" | "error" | "info" | "warning";

interface ToastApi {
  success: (msg: string) => void;
  error: (msg: string) => void;
  info: (msg: string) => void;
  warning: (msg: string) => void;
}

export interface ConfirmOptions {
  title: string;
  /** Texte ou contenu : ce qui va se passer, en clair. */
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Action destructrice : bouton rouge. */
  danger?: boolean;
  /** Mot à retaper pour confirmer (actions coûteuses ou irréversibles). */
  typeToConfirm?: string;
}

const ToastContext = React.createContext<ToastApi | null>(null);
const ConfirmContext = React.createContext<((o: ConfirmOptions) => Promise<boolean>) | null>(null);

export const FeedbackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toast, setToast] = React.useState<{ key: number; msg: string; severity: Severity } | null>(null);
  const [confirm, setConfirm] = React.useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  const [typed, setTyped] = React.useState("");

  const toastApi = React.useMemo<ToastApi>(() => {
    const push = (severity: Severity) => (msg: string) => setToast({ key: Date.now(), msg, severity });
    return { success: push("success"), error: push("error"), info: push("info"), warning: push("warning") };
  }, []);

  const ask = React.useCallback(
    (o: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setTyped("");
        setConfirm({ ...o, resolve });
      }),
    [],
  );

  const close = (v: boolean) => {
    confirm?.resolve(v);
    setConfirm(null);
  };
  const blocked = Boolean(confirm?.typeToConfirm) && typed.trim() !== confirm?.typeToConfirm;

  return (
    <ToastContext.Provider value={toastApi}>
      <ConfirmContext.Provider value={ask}>
        {children}

        <Snackbar
          key={toast?.key}
          open={Boolean(toast)}
          autoHideDuration={toast?.severity === "error" ? 8000 : 4000}
          onClose={(_, reason) => reason !== "clickaway" && setToast(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={toast?.severity ?? "info"}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ minWidth: 320, boxShadow: 6 }}
          >
            {toast?.msg}
          </Alert>
        </Snackbar>

        <Dialog open={Boolean(confirm)} onClose={() => close(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{confirm?.title}</DialogTitle>
          <DialogContent>
            {typeof confirm?.message === "string" ? (
              <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                {confirm.message}
              </Typography>
            ) : (
              confirm?.message
            )}
            {confirm?.typeToConfirm && (
              <TextField
                autoFocus
                fullWidth
                sx={{ mt: 2 }}
                label={`Tapez « ${confirm.typeToConfirm} » pour confirmer`}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => close(false)} color="inherit">
              {confirm?.cancelLabel ?? "Annuler"}
            </Button>
            <Button
              variant="contained"
              color={confirm?.danger ? "error" : "primary"}
              onClick={() => close(true)}
              disabled={blocked}
              autoFocus={!confirm?.typeToConfirm}
            >
              {confirm?.confirmLabel ?? "Confirmer"}
            </Button>
          </DialogActions>
        </Dialog>
      </ConfirmContext.Provider>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastApi => {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast hors de <FeedbackProvider>");
  return ctx;
};

export const useConfirm = () => {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm hors de <FeedbackProvider>");
  return ctx;
};

/** Message lisible d'une erreur axios (champ `error` du serveur d'abord). */
export const errorMessage = (e: unknown, fallback = "Une erreur est survenue."): string => {
  const err = e as { response?: { data?: { error?: string; message?: string } }; message?: string };
  return String(err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback);
};
